import React, { useEffect, useState } from "react";
import axios from "axios";
import useViewport from "components/UseViewport/UseViewport";
import MobileTable from "components/MobileTable/MobileTable";
import { Table, Button, Modal, Menu, Dropdown, Card } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";
import { NoShow } from "interfaces/events";
import { message } from "components/Messages/Messages";

const NoShows = ({ studentId, newTermViolation }: { studentId: number; newTermViolation: NoShow | undefined }) => {
  const [noShows, setNoShows] = useState<NoShow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [newNoShow, setNewNoShow] = useState<NoShow | undefined>(newTermViolation);
  const { width } = useViewport();
  const history = useHistory();

  useEffect(() => {
    setLoading(true);
    axios
      .get<NoShow[]>(`/events/student-no-shows/${studentId}/`)
      .then(response => {
        setNoShows(response.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [studentId]);

  useEffect(() => {
    if (newTermViolation) {
      setNewNoShow(newTermViolation);
    }
  }, [newTermViolation]);

  useEffect(() => {
    if (newNoShow && !noShows.some(noShow => noShow.id === newNoShow.id)) {
      setNoShows(prevNoShows => [...prevNoShows, newNoShow]);
      setNewNoShow(undefined);
    }
  }, [newNoShow, noShows]);

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Er du sikker på at du vil slette denne prikken?",
      onOk: () => {
        axios
          .delete(`events/no-shows/${id}/`)
          .then(() => {
            message("success", "Prikken er slettet");
            setNoShows(noShows.filter(noShow => noShow.id !== id));
          })
          .catch(() => {
            message("error", "Det oppsto en feil ved sletting av prikk");
          });
      },
      okText: "Slett",
      cancelText: "Avbryt",
      okButtonProps: {
        danger: true,
      },
    });
  };

  const columns = [
    {
      title: "Arrangement",
      dataIndex: "event_name",
      key: "eventName",
      render: (name: string, record: NoShow) => {
        return <Link to={`/admin/events/${record.event}`}>{name}</Link>;
      },
    },
    {
      title: "Type",
      dataIndex: "no_show_type",
      key: "noShowType",
      render: (type: number) => {
        if (type === 0) return "No Show";
        if (type === 1) return "Prikk";
        return "Ukjent";
      },
    },
    {
      title: "Utestengt",
      dataIndex: "got_banned",
      key: "got_banned",
      render: (got_banned: boolean) => (got_banned ? "Ja" : "Nei"),
    },
    {
      title: "Tidspunkt",
      dataIndex: "time",
      key: "time",
      render: (time: string) =>
        new Date(time).toLocaleString("nb-NO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
    {
      title: "",
      dataIndex: "id",
      key: "action",
      render: (id: number) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item key="1" onClick={() => handleDelete(id)} danger>
                Slett
              </Menu.Item>
            </Menu>
          }
        >
          <Button icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <Card title="Prikker">
      {width > 768 ? (
        <Table
          columns={columns}
          dataSource={noShows}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: "Studenten har ingen prikker" }}
        />
      ) : (
        <MobileTable
          data={noShows}
          columns={[
            {
              title: "Arrangement",
              dataIndex: "event_name",
              key: "event_name",
            },
            {
              title: "Type",
              dataIndex: "no_show_type",
              key: "no_show_type",
              render: no_show_type => {
                if (no_show_type === 0) return "No Show";
                if (no_show_type === 1) return "Prikk";
                return "Ukjent";
              },
            },
            {
              title: "Utestengt",
              dataIndex: "got_banned",
              key: "got_banned",
              render: got_banned => (got_banned ? "Ja" : "Nei"),
            },
            {
              title: "Tidspunkt",
              dataIndex: "time",
              key: "time",
              render: time =>
                new Date(time as string).toLocaleString("nb-NO", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
            },
          ]}
          loading={loading}
          onClick={id => history.push(`/admin/events/${id}/`)}
          emptyText="Studenten har ingen prikker"
        />
      )}
    </Card>
  );
};

export default NoShows;
